import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useShowContext } from 'framework/show';
import type { ValidationSchema } from 'validation-schema-library';
import type { StaffingDataType } from 'domain/shared/staffing';
import type { ProjectTeamMemberDto } from 'domain/shared/newProject';
import { filterToDateRange, buildTimelineDays, getVisibleHeatmapTeamMembers } from './helpers';
import type {
  DateRange,
  HeatmapSection,
  HeatmapTimelineData,
  HeatmapCriticalPeriodsBlock,
  HeatmapLeavesBlock,
} from './types';
import { UiThemeOverrider } from 'framework/uiTheme';
import { useHeatmapCriticalPeriods } from './useHeatmapCriticalPeriods';
import { useHeatmapLeaves } from './useHeatmapLeaves';
import { EmployeeAliasesFilter, getSelectedEmployeeAliases } from './filter/employeeAliasesFilter';
import { DateRangeFilter, DateRangeFilterLayout, Filter, ListFiltersLayout } from 'framework/list';

const HEATMAP_FILTER_SOURCE = 'dateRange';

const getBudgetDateRange = (
  project: { budget?: { startDate?: string; endDate?: string } } | null,
): DateRange | null => {
  const startDate = project?.budget?.startDate;
  const endDate = project?.budget?.endDate;
  if (!startDate || !endDate) return null;
  return { start: startDate, end: endDate };
};

const getSowFilterValidationSchema = (sowStart: string, sowEnd: string): ValidationSchema => ({
  [HEATMAP_FILTER_SOURCE]: {
    ge: { minDate: sowStart, maxDate: sowEnd },
    le: { minDate: sowStart, maxDate: sowEnd },
  },
});

const buildHeatmapSections = (
  criticalPeriods: HeatmapCriticalPeriodsBlock,
  leaves: HeatmapLeavesBlock,
): HeatmapSection[] => [
  {
    id: 'criticalPeriods',
    title: 'Critical periods',
    collapsible: false,
    summaryBars: criticalPeriods.bars,
  },
  {
    id: 'leaves',
    title: 'Leaves',
    collapsible: true,
    summaryBars: leaves.summaryBars,
    rows: leaves.rows,
  },
];

export const useProjectHeatmap = () => {
  const { data: showData } = useShowContext<StaffingDataType>();
  const project = showData?.getData?.() ?? showData ?? null;
  const [filters, setFilters] = useState<Filter>({});

  const sowRange = useMemo(() => {
    const startDate = project?.budget?.startDate;
    const endDate = project?.budget?.endDate;
    if (!startDate || !endDate) return null;
    return { ge: startDate, le: endDate };
  }, [project]);

  useEffect(() => {
    if (!sowRange) return;
    setFilters(prev => {
      const hasDateRange = prev[HEATMAP_FILTER_SOURCE] as { ge?: string; le?: string } | undefined;
      if (hasDateRange?.ge && hasDateRange?.le) return prev;
      return { ...prev, [HEATMAP_FILTER_SOURCE]: { ge: sowRange.ge, le: sowRange.le } };
    });
  }, [sowRange]);

  const getValidationSchema = useCallback((): Promise<ValidationSchema> => {
    if (!sowRange) return Promise.resolve({});
    return Promise.resolve(getSowFilterValidationSchema(sowRange.ge, sowRange.le));
  }, [sowRange]);

  const dateRange = useMemo(
    () => filterToDateRange(filters, HEATMAP_FILTER_SOURCE) ?? getBudgetDateRange(project),
    [filters, project],
  );
  const projectDateRange = useMemo(() => getBudgetDateRange(project), [project]);

  const selectedEmployeeAliases = useMemo(() => getSelectedEmployeeAliases(filters), [filters]);

  const visibleTeamMembers = useMemo(
    () => getVisibleHeatmapTeamMembers((project?.team?.members ?? []) as ProjectTeamMemberDto[]),
    [project?.team?.members],
  );

  const { criticalPeriodsBlock } = useHeatmapCriticalPeriods({ project, dateRange });
  const { leavesBlock, isLoadingLeaves } = useHeatmapLeaves({
    project,
    projectDateRange,
    selectedEmployeeAliases,
  });

  const heatmapData = useMemo((): HeatmapTimelineData | null => {
    if (!project || !dateRange) return null;
    const days = buildTimelineDays(dateRange);
    if (days.length === 0) return null;
    return {
      days,
      sections: buildHeatmapSections(criticalPeriodsBlock, leavesBlock),
    };
  }, [project, dateRange, criticalPeriodsBlock, leavesBlock]);

  const contextValue = useMemo(
    () => ({
      data: { items: [], heatmapData, isLoadingLeaves },
      loading: false,
      filter: filters,
      filterBy: setFilters,
    }),
    [filters, heatmapData, isLoadingLeaves],
  );

  const heatmapFilters = (
    <UiThemeOverrider
      overriddenLayouts={{
        DateRangeFilterLayout,
      }}
    >
      <ListFiltersLayout>
        <DateRangeFilter canBeInFuture source={HEATMAP_FILTER_SOURCE} label="Period" />
        <EmployeeAliasesFilter members={visibleTeamMembers} />
      </ListFiltersLayout>
    </UiThemeOverrider>
  );

  return {
    contextValue,
    heatmapFilters,
    getValidationSchema,
  };
};
